/**
 * Drill Import/Export Utilities
 * 
 * Centralized module for all drill import and export functionality.
 * This library provides comprehensive tools for importing and exporting drills
 * with support for CSV format, duplicate detection, and progress tracking.
 */

import { Drill } from '../types/drill-types';
import { exportDrillsToCSV } from '@/utils/drill-export-csv';
import { validateImportFile as validateImportFileUtil, readFileAsText as readFileAsTextUtil, isDuplicateDrill, type DrillImportData as DrillImportDataUtil, type ImportMode as ImportModeUtil } from '@/utils/drillImport';
import { loadTaxonomy } from '@/lib/taxonomy-loader';
import { DrillOperations } from './drill-operations';

// Re-export types for convenience
export type DrillImportData = DrillImportDataUtil;
export type ImportMode = ImportModeUtil;

/**
 * Export drills to CSV file
 * Automatically loads taxonomy and formats data correctly
 */
export async function exportDrillsToFile(drills: Drill[]): Promise<void> {
  try {
    const taxonomyData = await loadTaxonomy();
    const taxonomy = taxonomyData.categories || {};
    exportDrillsToCSV(drills, taxonomy);
  } catch (error) {
    throw new Error(`Failed to export drills: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validate and parse import file (CSV or JSON)
 * Re-export from utils for convenience
 */
export const validateImportFile = validateImportFileUtil;

/**
 * Read file as text
 * Re-export from utils for convenience
 */
export const readFileAsText = readFileAsTextUtil;

/**
 * Get duplicate drills from import data
 */
export function getDuplicateDrills(
  importData: DrillImportData,
  existingDrills: Drill[]
): Drill[] {
  const duplicates: Drill[] = [];
  
  for (const importDrill of importData.drills) {
    const isDuplicate = existingDrills.some(existing => 
      isDuplicateDrill(importDrill, existing)
    );
    
    if (isDuplicate) {
      duplicates.push(importDrill);
    }
  }
  
  return duplicates;
}

/**
 * Get import summary statistics
 */
export function getImportSummary(importData: DrillImportData): {
  totalDrills: number;
  duplicateCount: number;
  exportedAt?: string;
  version?: string;
} {
  return {
    totalDrills: importData.drills.length,
    duplicateCount: importData.duplicateCount,
    exportedAt: importData.exportedAt,
    version: importData.version
  };
}

/**
 * Import drills from parsed data
 * Performs the actual import of parsed drill data to Firebase
 * 
 * Note: This function requires a DrillOperations instance to perform the actual database write.
 * In React components, use useDrillOperations() hook to get the operations instance.
 * 
 * @deprecated This function has limitations. Use DrillOperations.importFromFile() directly instead.
 */
export async function importDrillsFromData(
  fileData: DrillImportData,
  existingDrills: Drill[],
  importMode: ImportMode,
  drillOps?: DrillOperations,
  onProgress?: (progress: number) => void
): Promise<{ success: number; errors: string[] }> {
  if (!drillOps) {
    throw new Error(
      'importDrillsFromData requires a DrillOperations instance. ' +
      'Use DrillOperations.importFromFile() directly, or use performDrillImport() which handles file reading.'
    );
  }
  
  // This function cannot work without file content to reconstruct the import
  // DrillOperations.importFromFile requires the original file content
  throw new Error(
    'importDrillsFromData requires file content. ' +
    'Use DrillOperations.importFromFile() directly with the file content, or use performDrillImport() which handles file reading.'
  );
}

/**
 * Complete import workflow (recommended)
 * Handles file reading, validation, and import in one call
 * 
 * Note: This function requires a DrillOperations instance to perform the actual database write.
 * In React components, use useDrillOperations() hook to get the operations instance.
 */
export async function performDrillImport(
  file: File,
  existingDrills: Drill[],
  importMode: ImportMode,
  drillOps?: DrillOperations,
  onProgress?: (progress: number) => void
): Promise<{
  validationError?: string;
  importData?: DrillImportData;
  result?: { success: number; errors: string[] };
}> {
  try {
    // Read file
    onProgress?.(10);
    const content = await readFileAsText(file);
    
    // Validate and parse
    onProgress?.(30);
    const importData = await validateImportFile(content, file.name);
    
    // If no drillOps provided, return parsed data without importing
    if (!drillOps) {
      onProgress?.(100);
      return {
        importData,
        result: {
          success: 0,
          errors: ['DrillOperations instance required for import. Use useDrillOperations() hook in React components.']
        }
      };
    }
    
    // Perform the actual import using DrillOperations
    onProgress?.(50);
    const mode = importMode === 'skip' ? 'skip' : importMode === 'reimport' ? 'reimport' : 'overwrite';
    const importResult = await drillOps.importFromFile(
      content,
      file.name,
      mode,
      (progress) => {
        // Map progress from 50-100 range to overall 50-100 range
        const mappedProgress = 50 + (progress * 0.5);
        onProgress?.(mappedProgress);
      }
    );
    
    onProgress?.(100);
    return {
      importData,
      result: {
        success: importResult.success + importResult.updated,
        errors: importResult.errors
      }
    };
  } catch (error) {
    return {
      validationError: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

